import type { EDIEventType, EDIDocumentEvents } from '../types/events';

type EventCallback = (event: CustomEvent) => void;

class EventBus {
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private element: HTMLElement | null = null;

  setElement(element: HTMLElement) {
    this.element = element;
  }

  on<K extends EDIEventType>(eventType: K, callback: (event: EDIDocumentEvents[K]) => void) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback as EventCallback);
  }

  off<K extends EDIEventType>(eventType: K, callback: (event: EDIDocumentEvents[K]) => void) {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.delete(callback as EventCallback);
    }
  }

  emit<K extends EDIEventType>(eventType: K, detail: EDIDocumentEvents[K]['detail']) {
    const event = new CustomEvent(eventType, {
      detail,
      bubbles: true,
      composed: true,
    });

    // Dispatch to internal listeners
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach((callback) => callback(event));
    }

    // Dispatch to DOM element if available (for web component)
    if (this.element) {
      this.element.dispatchEvent(event);
    }

    // Also log for debugging in development
    if (import.meta.env.DEV) {
      console.log(`[EventBus] ${eventType}`, detail);
    }
  }

  clear() {
    this.listeners.clear();
  }
}

export const eventBus = new EventBus();
